import { useEffect, useState } from "react";
import { Download, Loader2, MonitorSmartphone, MousePointerClick, CheckCircle2, XCircle, Users, PlayCircle, Eye, Megaphone } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg, fmtINR } from "../../lib/api";
import { PageHeader, StatCard, LoadingRows, TableShell, TR, TD, EmptyState } from "../../components/Shared";
import { Button } from "../../components/ui/button";

const RANGES = [["1", "Today"], ["7", "7 Days"], ["30", "30 Days"], ["90", "90 Days"]];

export default function RootAppAnalytics() {
  const [days, setDays] = useState("30");
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = (d = days) => {
    setData(null);
    api.get("/root/app-pwa/analytics", { params: { days: d } }).then((r) => setData(r.data)).catch((e) => { toast.error(errMsg(e)); setData({}); });
  };
  useEffect(() => { load(days); }, [days]); // eslint-disable-line

  const exportCsv = () => {
    if (!data) return;
    const rows = [["metric", "value"],
      ["install_banner_views", data.install.banner_views], ["install_button_clicks", data.install.button_clicks],
      ["prompt_accepted", data.install.prompt_accepted], ["prompt_dismissed", data.install.prompt_dismissed],
      ["confirmed_installs", data.install.confirmed_installs],
      ["active_today", data.engagement.active_today], ["active_7d", data.engagement.active_7d],
      ["sessions", data.engagement.sessions], ["video_views", data.engagement.video_views],
      ["video_starts", data.engagement.video_starts], ["video_completes", data.engagement.video_completes],
      ["ad_impressions", data.ads.impressions], ["ad_clicks", data.ads.clicks], ["ad_ctr_%", data.ads.ctr]];
    const csv = rows.map((r) => r.join(",")).join("\n");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    a.download = `miltroo-app-analytics-${days}d.csv`;
    a.click();
  };

  if (!data) return <LoadingRows rows={4} />;
  const i = data.install, e = data.engagement, a = data.ads;
  const maxSeries = Math.max(1, ...(data.series || []).map((s) => s.active_users));

  return (
    <div className="space-y-6" data-testid="root-app-analytics">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <PageHeader title="App Analytics" subtitle="Install funnel, engagement and advertising — real tracked events only" testid="app-analytics-title" />
        <div className="flex items-center gap-2">
          <div className="flex rounded-full border border-border overflow-hidden" data-testid="app-analytics-ranges">
            {RANGES.map(([v, l]) => (
              <button key={v} onClick={() => setDays(v)} data-testid={`range-${v}`}
                className={`px-4 h-9 text-xs font-bold transition-colors ${days === v ? "bg-emerald-600 text-white" : "text-muted-foreground hover:bg-muted"}`}>{l}</button>
            ))}
          </div>
          <Button variant="outline" size="sm" onClick={exportCsv} data-testid="app-analytics-csv" className="rounded-full font-bold"><Download className="h-4 w-4 mr-1" />CSV</Button>
        </div>
      </div>

      <div>
        <h2 className="font-display font-bold text-lg mb-3 flex items-center gap-2"><MonitorSmartphone className="h-5 w-5 text-emerald-500" />Install Funnel</h2>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard icon={Eye} label="Banner Views" value={i.banner_views} testid="an-banner-views" tint="bg-blue-500/10 text-blue-500" />
          <StatCard icon={MousePointerClick} label={`Button Clicks (${i.click_rate}%)`} value={i.button_clicks} testid="an-banner-clicks" delay={0.05} tint="bg-amber-500/10 text-amber-500" />
          <StatCard icon={CheckCircle2} label="Prompt Accepted" value={i.prompt_accepted} testid="an-accepted" delay={0.1} tint="bg-emerald-500/10 text-emerald-500" />
          <StatCard icon={Download} label="Confirmed Installs" value={i.confirmed_installs} testid="an-installs" delay={0.15} tint="bg-pink-500/10 text-pink-500" />
        </div>
        <p className="text-[11px] text-muted-foreground mt-2" data-testid="an-install-note">{i.note}</p>
      </div>

      <div>
        <h2 className="font-display font-bold text-lg mb-3 flex items-center gap-2"><Users className="h-5 w-5 text-emerald-500" />Engagement</h2>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard icon={Users} label="Active Today" value={e.active_today} testid="an-dau" tint="bg-emerald-500/10 text-emerald-500" />
          <StatCard icon={Users} label="Active (7d)" value={e.active_7d} testid="an-wau" delay={0.05} tint="bg-teal-500/10 text-teal-500" />
          <StatCard icon={PlayCircle} label="Video Views" value={e.video_views} testid="an-views" delay={0.1} tint="bg-violet-500/10 text-violet-500" />
          <StatCard icon={PlayCircle} label="Video Starts / Completes" value={`${e.video_starts} / ${e.video_completes}`} testid="an-vstarts" delay={0.15} tint="bg-blue-500/10 text-blue-500" />
        </div>
      </div>

      {(data.series || []).length > 0 && (
        <div className="rounded-2xl border border-border p-5" data-testid="an-series">
          <p className="font-bold text-sm mb-4">Daily Active Users</p>
          <div className="flex items-end gap-1.5 h-32">
            {data.series.map((s) => (
              <div key={s.day} className="flex-1 flex flex-col items-center gap-1 min-w-0">
                <div className="w-full rounded-t bg-emerald-600/80" style={{ height: `${Math.max(3, (s.active_users / maxSeries) * 100)}%` }} title={`${s.day}: ${s.active_users} users, ${s.installs} installs`} />
                <span className="text-[9px] text-muted-foreground truncate w-full text-center">{s.day.slice(5)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div>
        <h2 className="font-display font-bold text-lg mb-3 flex items-center gap-2"><Megaphone className="h-5 w-5 text-emerald-500" />Advertising</h2>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard icon={Eye} label="Ad Impressions" value={a.impressions} testid="an-ad-imp" tint="bg-amber-500/10 text-amber-500" />
          <StatCard icon={MousePointerClick} label="Ad Clicks" value={a.clicks} testid="an-ad-clicks" delay={0.05} tint="bg-emerald-500/10 text-emerald-500" />
          <StatCard icon={CheckCircle2} label="CTR" value={`${a.ctr}%`} testid="an-ad-ctr" delay={0.1} tint="bg-blue-500/10 text-blue-500" />
          <StatCard icon={XCircle} label="Revenue" value="—" testid="an-ad-rev" delay={0.15} tint="bg-muted text-muted-foreground" />
        </div>
        <p className="text-[11px] text-muted-foreground mt-2" data-testid="an-ads-note">{a.revenue_note}</p>
      </div>

      <div>
        <h2 className="font-display font-bold text-lg mb-3">Top Videos</h2>
        {(data.top_videos || []).length === 0 ? <EmptyState title="No video data" body="Views appear here once videos are watched." /> : (
          <TableShell head={["Video", "Creator", "Views"]} testid="an-top-videos">
            {data.top_videos.map((v) => (
              <TR key={v.video_id} testid={`an-video-${v.video_id}`}>
                <TD><span className="font-semibold text-sm">{v.title}</span></TD>
                <TD><span className="text-xs">{v.creator}</span></TD>
                <TD><span className="font-bold">{v.views}</span></TD>
              </TR>
            ))}
          </TableShell>
        )}
      </div>
    </div>
  );
}
